import { Button } from '@/ui';

import { ImagePreview } from '@/components/blueprint-form/subcomponents/image-preview';
import { useRenderFpsr } from '@/hooks';
import {
    DEFAULT_FPSR_BACKGROUND,
    flatPathToBlueprintPath,
} from '@/lib/fpsr';
import { FormControl, FormField, FormItem } from '@/ui';
import { BlueprintUtils } from '@/utils';
import type { RenderBackground } from '@rickyzhangca/fpsr';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
    ICreateBlueprintFormValues,
    useBlueprintFormStore,
} from '../BlueprintForm';
import { ImageFpsrBackground } from './image-fpsr-background';

export const ImageFPSR = () => {
  const { mutateAsync, isPending, isError, error, reset } = useRenderFpsr();

  const form = useFormContext<ICreateBlueprintFormValues>();
  const imageFile = form.watch('image_file');
  const imageUrl = form.watch('image_url');
  const blueprintString = form.watch('blueprint_string');
  const fpsrBackground =
    form.watch('fpsr_background') ?? DEFAULT_FPSR_BACKGROUND;

  const setImageType = useBlueprintFormStore(state => state.setImageType);
  const setFpsrPath = useBlueprintFormStore(state => state.setFpsrPath);
  const setIsFpsrTagged = useBlueprintFormStore(
    state => state.setIsFpsrTagged
  );
  const imageType = useBlueprintFormStore(state => state.imageType);
  const selectedPath = useBlueprintFormStore(state => state.selectedFpsrPath);
  const setSelectedPath = useBlueprintFormStore(
    state => state.setSelectedFpsrPath
  );

  const blueprintData = useBlueprintFormStore(state => state.blueprintData);
  const type = useMemo(
    () => BlueprintUtils.Analysis.getBlueprintType(blueprintData),
    [blueprintData]
  );
  const blueprintList = useMemo(
    () => BlueprintUtils.Analysis.getFlatBlueprintList(blueprintData),
    [blueprintData]
  );

  const [lastRenderedKey, setLastRenderedKey] = useState<string | null>(null);
  const prevSelectedPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (form.getValues('fpsr_background') == null) {
      form.setValue('fpsr_background', DEFAULT_FPSR_BACKGROUND);
    }
  }, [form]);

  useEffect(() => {
    const firstPath =
      blueprintList.find(item => item.type === 'blueprint')?.path ?? null;
    setSelectedPath(prev => {
      const stillValid =
        prev != null &&
        blueprintList.some(
          item => item.path === prev && item.type === 'blueprint'
        );
      return stillValid ? prev : firstPath;
    });
    reset();
  }, [blueprintList, reset, setSelectedPath]);

  // Edit: existing FPSR thumbnail counts as already rendered for the current string.
  useEffect(() => {
    if (!imageUrl || imageFile || !blueprintString) return;
    setLastRenderedKey(
      prev => prev ?? `${blueprintString}|${selectedPath ?? ''}`
    );
  }, [imageUrl, imageFile, blueprintString, selectedPath]);

  const selectedItem = useMemo(
    () => blueprintList.find(item => item.path === selectedPath) ?? null,
    [blueprintList, selectedPath]
  );

  const renderKey = `${blueprintString}|${selectedPath ?? ''}`;
  const showRenderButton = lastRenderedKey !== renderKey;

  const handleRender = useCallback(
    async (background: RenderBackground = fpsrBackground) => {
      if (!selectedItem || !blueprintString) return;
      const blueprintPath = flatPathToBlueprintPath(selectedItem.path);
      try {
        const file = await mutateAsync({
          blueprintString,
          blueprintPath,
          background,
        });
        form.setValue('image_file', file);
        form.setValue('image_url', undefined);
        setImageType('fpsr');
        setIsFpsrTagged(true);
        setFpsrPath(blueprintPath ?? null);
        setLastRenderedKey(`${blueprintString}|${selectedItem.path}`);
      } catch {
        // error surfaced via isError / error from the mutation
      }
    },
    [
      selectedItem,
      blueprintString,
      fpsrBackground,
      mutateAsync,
      form,
      setImageType,
      setIsFpsrTagged,
      setFpsrPath,
    ]
  );

  // Auto-render when the user picks a different blueprint in the glance tree.
  useEffect(() => {
    const prev = prevSelectedPathRef.current;
    prevSelectedPathRef.current = selectedPath;

    if (prev == null || selectedPath == null || prev === selectedPath) return;
    if (imageType !== 'fpsr') return;
    if (!selectedItem || !blueprintString) return;

    void handleRender();
  }, [selectedPath, imageType, selectedItem, blueprintString, handleRender]);

  const handleBackgroundChange = useCallback(
    (background: RenderBackground) => {
      void handleRender(background);
    },
    [handleRender]
  );

  return (
    <FormField
      name="image_file"
      render={() => (
        <FormItem className="flex flex-col gap-4 space-y-0 rounded-lg bg-steel-800 p-4">
          <ImageFpsrBackground onChange={handleBackgroundChange} />
          <ImagePreview
            file={imageFile}
            url={imageFile ? undefined : imageUrl}
            placeholder={
              !selectedItem
                ? blueprintList.length > 0
                  ? type === 'blueprint_book'
                    ? 'Choose a blueprint in the book to render as the screenshot.'
                    : 'Only blueprints and blueprints in blueprint books are supported for now'
                  : 'Please paste a blueprint string first'
                : [
                    isError
                      ? `Something went wrong rendering this blueprint.${
                          error?.message ? `\nError: "${error.message}"` : ''
                        }`
                      : isPending
                        ? 'Rendering... It may take a few seconds depending on the blueprint size'
                        : 'Click to render.',
                    type === 'blueprint_book' &&
                      `Selected blueprint: ${selectedItem.label}\nExpand the book to change the selected blueprint`,
                  ]
                    .filter(Boolean)
                    .join('\n')
            }
          />
          {showRenderButton && (
            <FormControl>
              <Button
                disabled={!selectedItem || !blueprintString}
                loading={isPending}
                type="button"
                onClick={() => void handleRender()}
                data-umami-event="render-fpsr"
              >
                {isPending
                  ? 'Rendering...'
                  : lastRenderedKey != null
                    ? 'Render again'
                    : 'Render'}
                <p className="ml-2 inline-flex w-fit -translate-y-px rounded bg-steel-950 px-1 py-0.5 text-xs font-bold text-fern-400">
                  BETA
                </p>
              </Button>
            </FormControl>
          )}
        </FormItem>
      )}
    />
  );
};
